import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Users,
  Loader2,
  Edit,
  Search,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  CalendarDays,
  TrendingUp,
  ArrowLeftRight,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useEntitlements, useEntitlementActions } from '@/hooks/useEntitlements';
import { motion } from 'framer-motion';

// Stat Pill Component
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" }
  };
  const cfg = colorConfig[color];

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${cfg.bg} border border-slate-200/50`}>
      <div className={`p-2 rounded-lg ${cfg.icon}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className={`text-xl font-bold ${cfg.value}`}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
};

// Entitlement Row Component
const EntitlementRow = ({ entitlement, getUserName, getTotalDays, onEdit, onDelete }) => {
  const total = getTotalDays(entitlement);
  const usagePercent = total > 0 ? Math.round((entitlement.taken_days / total) * 100) : 0;

  return (
    <div className="group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-md hover:border-slate-300">
      <div className="absolute left-0 top-3 bottom-3 w-1 bg-indigo-500 rounded-full" />

      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-semibold text-sm">
            {entitlement.user?.first_name?.[0]}{entitlement.user?.last_name?.[0]}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-slate-800">{getUserName(entitlement.user)}</h3>
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
              {entitlement.year}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-3">{entitlement.user?.email}</p>

          {/* Stats */}
          <div className="flex gap-6 text-sm mb-3">
            <div>
              <p className="text-slate-500 text-xs">Entitled</p>
              <p className="font-semibold text-slate-800">{total} days</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Taken</p>
              <p className="font-semibold text-amber-600">{entitlement.taken_days} days</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">Remaining</p>
              <p className="font-semibold text-emerald-600">{entitlement.remaining_days} days</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Leave Usage</span>
              <span className="font-medium text-slate-600">{usagePercent}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  usagePercent > 90 ? 'bg-rose-500' : usagePercent > 75 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => onEdit(entitlement)} className="h-8 w-8 p-0 rounded-lg">
            <Edit className="h-4 w-4 text-slate-500" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(entitlement)} className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EntitlementsPage = () => {
  const navigate = useNavigate();
  const { userProfile, can, loading: authLoading, permissionsLoaded } = useAuth();

  usePageTitle('Leave Entitlements');

  const authReady = !authLoading && permissionsLoaded;

  useEffect(() => {
    if (authReady && !can('attendance.manage')) {
      toast.error('You do not have permission to manage entitlements');
      navigate('/dashboard');
    }
  }, [authReady, can, navigate]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  const [deletingEntitlement, setDeletingEntitlement] = useState(null);
  const [memberSelectOpen, setMemberSelectOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_ids: [],
    year: new Date().getFullYear(),
    annual_leave_days: 25,
    carry_over_days: 0,
    entitled_to_bank_holidays: true,
    custom_entitlements: {}
  });

  const {
    entitlements,
    teamMembers,
    customTypes,
    stats,
    loading,
    error,
    pagination,
    filters,
    getTotalDays,
    refetch,
    years
  } = useEntitlements(userProfile?.company_id);

  const { createEntitlement, createEntitlementsBulk, updateEntitlement, deleteEntitlement, processing } = useEntitlementActions(refetch);

  const openCreateDialog = useCallback(() => {
    setEditingEntitlement(null);
    const defaultCustomEntitlements = {};
    customTypes.forEach(type => {
      defaultCustomEntitlements[type.id] = type.default_days;
    });
    setFormData({
      user_ids: [],
      year: parseInt(filters.year) || new Date().getFullYear(),
      annual_leave_days: 25,
      carry_over_days: 0,
      entitled_to_bank_holidays: true,
      custom_entitlements: defaultCustomEntitlements
    });
    setDialogOpen(true);
  }, [customTypes, filters.year]);

  const openEditDialog = useCallback((entitlement) => {
    setEditingEntitlement(entitlement);
    const customEntitlements = {};
    customTypes.forEach(type => {
      customEntitlements[type.id] = entitlement.custom_entitlements?.[type.id] ?? type.default_days;
    });
    setFormData({
      user_ids: [entitlement.user_id],
      year: entitlement.year,
      annual_leave_days: entitlement.annual_leave_days,
      carry_over_days: entitlement.carry_over_days || 0,
      entitled_to_bank_holidays: entitlement.entitled_to_bank_holidays ?? true,
      custom_entitlements: customEntitlements
    });
    setDialogOpen(true);
  }, [customTypes]);

  const handleSave = useCallback(async () => {
    if (!formData.user_ids || formData.user_ids.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }

    if (formData.annual_leave_days < 0) {
      toast.error('Annual leave days cannot be negative');
      return;
    }

    if (editingEntitlement) {
      const result = await updateEntitlement(editingEntitlement.id, {
        ...formData,
        user_id: formData.user_ids[0]
      });
      if (result.success) {
        toast.success('Entitlement updated');
        setDialogOpen(false);
        setEditingEntitlement(null);
      } else {
        toast.error(result.error || 'Failed to update');
      }
    } else {
      const result = await createEntitlementsBulk(formData.user_ids, formData);
      if (result.success) {
        if (result.created > 0 && result.skipped === 0) {
          toast.success(`Created ${result.created} entitlement${result.created > 1 ? 's' : ''}`);
          setDialogOpen(false);
        } else if (result.created > 0 && result.skipped > 0) {
          toast.warning(`Created ${result.created}, skipped ${result.skipped} (already exist)`);
          setDialogOpen(false);
        } else if (result.created === 0 && result.skipped > 0) {
          toast.error('All selected users already have entitlements for this year');
        } else {
          setDialogOpen(false);
        }
      } else {
        toast.error(result.errors?.[0] || 'Failed to create');
      }
    }
  }, [formData, editingEntitlement, createEntitlementsBulk, updateEntitlement]);

  const openDeleteDialog = useCallback((entitlement) => {
    setDeletingEntitlement(entitlement);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deletingEntitlement) return;

    const result = await deleteEntitlement(deletingEntitlement.id);
    if (result.success) {
      toast.success('Entitlement deleted');
      setDeleteDialogOpen(false);
      setDeletingEntitlement(null);
    } else {
      toast.error(result.error || 'Failed to delete');
    }
  }, [deletingEntitlement, deleteEntitlement]);

  const getUserName = useCallback((user) => {
    if (!user) return 'Unknown User';
    return `${user.first_name} ${user.last_name}`;
  }, []);

  const toggleUserSelection = useCallback((userId) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  }, []);

  const selectAllUsers = useCallback(() => {
    setFormData(prev => ({ ...prev, user_ids: teamMembers.map(m => m.id) }));
  }, [teamMembers]);

  const clearAllUsers = useCallback(() => {
    setFormData(prev => ({ ...prev, user_ids: [] }));
  }, []);

  const isAllSelected = useMemo(() => {
    return teamMembers.length > 0 && formData.user_ids.length === teamMembers.length;
  }, [teamMembers, formData.user_ids]);

  const selectedUserNames = useMemo(() => {
    return formData.user_ids
      .map(id => teamMembers.find(m => m.id === id))
      .filter(Boolean)
      .map(m => `${m.first_name} ${m.last_name}`);
  }, [formData.user_ids, teamMembers]);

  const formTotal = useMemo(() => {
    const customTotal = Object.values(formData.custom_entitlements)
      .reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    return formData.annual_leave_days + formData.carry_over_days + customTotal;
  }, [formData.annual_leave_days, formData.carry_over_days, formData.custom_entitlements]);

  const yearsArray = useMemo(() => {
    return years.map(y => parseInt(y.value, 10));
  }, [years]);

  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (!can('attendance.manage')) {
    return null;
  }

  if (loading && !entitlements.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading entitlements...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-rose-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-rose-500" />
          </div>
          <p className="text-slate-800 font-semibold">Failed to load entitlements</p>
          <p className="text-slate-500 text-sm mt-1">{error}</p>
          <Button onClick={refetch} className="mt-4 rounded-full">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Leave Entitlements</h1>
            <p className="text-slate-500 mt-1">Manage global leave entitlements for all team members</p>
          </div>

          <Button onClick={openCreateDialog} className="h-10 bg-slate-800 hover:bg-slate-700 rounded-full px-5">
            <Plus className="h-4 w-4 mr-2" />
            Add Entitlement
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatPill icon={Users} label="Total Entitlements" value={stats.total_entitlements} color="slate" />
          <StatPill icon={CalendarDays} label="Current Year" value={stats.current_year_count} color="indigo" />
          <StatPill icon={TrendingUp} label="Average Days" value={stats.average_days} color="emerald" />
          <StatPill icon={ArrowLeftRight} label="With Carry Over" value={stats.with_carry_over} color="amber" />
        </div>
      </motion.div>

      {/* Entitlements List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => filters.setSearch(e.target.value)}
              className="pl-10 h-10 rounded-full border-slate-200"
            />
          </div>
          <Select value={filters.year} onValueChange={filters.setYear}>
            <SelectTrigger className="w-full sm:w-[140px] h-10 rounded-full border-slate-200">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((year) => (
                <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            Showing {entitlements.length} of {pagination.filteredItems} entitlements
            {loading && <Loader2 className="inline h-4 w-4 ml-2 animate-spin" />}
          </p>
        </div>

        {/* Entitlements */}
        {entitlements.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-slate-50 flex items-center justify-center">
              <Users className="h-7 w-7 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No entitlements found</p>
            <p className="text-slate-400 text-sm mt-1">Create entitlements for your team to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entitlements.map((entitlement) => (
              <EntitlementRow
                key={entitlement.id}
                entitlement={entitlement}
                getUserName={getUserName}
                getTotalDays={getTotalDays}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className="rounded-full"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="rounded-full"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingEntitlement ? 'Edit Entitlement' : 'Add Entitlement'}</DialogTitle>
            <DialogDescription>
              {editingEntitlement ? 'Update the leave entitlement details.' : 'Create a new global leave entitlement.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Team Member{!editingEntitlement && 's'} *</Label>
              {editingEntitlement ? (
                <div className="flex h-10 w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm">
                  {selectedUserNames[0] || 'Unknown User'}
                </div>
              ) : (
                <Popover open={memberSelectOpen} onOpenChange={setMemberSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between h-auto min-h-10 py-2 rounded-lg"
                    >
                      <div className="flex flex-wrap gap-1 flex-1 text-left">
                        {formData.user_ids.length === 0 ? (
                          <span className="text-slate-400">Select team members...</span>
                        ) : formData.user_ids.length === teamMembers.length ? (
                          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                            All team members ({teamMembers.length})
                          </Badge>
                        ) : formData.user_ids.length > 3 ? (
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                            {formData.user_ids.length} selected
                          </Badge>
                        ) : (
                          selectedUserNames.map((name, i) => (
                            <Badge key={i} className="bg-slate-100 text-slate-700 border-slate-200 text-xs">{name}</Badge>
                          ))
                        )}
                      </div>
                      <ChevronDown className="ml-2 h-4 w-4 text-slate-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <div className="p-2 border-b border-slate-100 flex items-center justify-between">
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={selectAllUsers} disabled={isAllSelected}>
                        <Check className="h-3 w-3 mr-1" />Select All
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearAllUsers} disabled={formData.user_ids.length === 0}>
                        <X className="h-3 w-3 mr-1" />Clear
                      </Button>
                      <span className="text-xs text-slate-500">{formData.user_ids.length}/{teamMembers.length}</span>
                    </div>
                    <ScrollArea className="h-[250px]">
                      <div className="p-2 space-y-1">
                        {teamMembers.map((member) => {
                          const isSelected = formData.user_ids.includes(member.id);
                          return (
                            <div
                              key={member.id}
                              className={cn(
                                "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-slate-50",
                                isSelected && "bg-indigo-50"
                              )}
                              onClick={() => toggleUserSelection(member.id)}
                            >
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleUserSelection(member.id)} onClick={(e) => e.stopPropagation()} />
                              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-semibold text-xs">
                                  {member.first_name?.[0]}{member.last_name?.[0]}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{member.first_name} {member.last_name}</p>
                                <p className="text-xs text-slate-500 truncate">{member.email}</p>
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-indigo-600" />}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              )}
              {!editingEntitlement && (
                <p className="text-xs text-slate-500">Select multiple to create in bulk</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Year *</Label>
              <Select
                value={formData.year.toString()}
                onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
                disabled={!!editingEntitlement}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearsArray.map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Annual Leave Days *</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={formData.annual_leave_days}
                onChange={(e) => setFormData({ ...formData, annual_leave_days: parseFloat(e.target.value) || 0 })}
                className="rounded-lg"
              />
              <p className="text-xs text-slate-500">Standard annual leave allowance</p>
            </div>

            <div className="space-y-2">
              <Label>Carry Over Days</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={formData.carry_over_days}
                onChange={(e) => setFormData({ ...formData, carry_over_days: parseFloat(e.target.value) || 0 })}
                className="rounded-lg"
              />
              <p className="text-xs text-slate-500">Days carried over from previous year</p>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <Label>Bank Holiday Entitlement</Label>
                <p className="text-xs text-slate-500 mt-0.5">Entitled to paid bank holidays</p>
              </div>
              <Switch
                checked={formData.entitled_to_bank_holidays}
                onCheckedChange={(checked) => setFormData({ ...formData, entitled_to_bank_holidays: checked })}
              />
            </div>

            {customTypes.length > 0 && (
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <Label className="text-slate-800">Custom Entitlements</Label>
                {customTypes.map((type) => (
                  <div key={type.id} className="space-y-1.5">
                    <Label className="text-sm text-slate-600">
                      {type.name}
                      {type.description && <span className="font-normal text-slate-400 ml-2">({type.description})</span>}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.custom_entitlements[type.id] || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        custom_entitlements: { ...formData.custom_entitlements, [type.id]: parseFloat(e.target.value) || 0 }
                      })}
                      className="rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Total Preview */}
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-indigo-800">Total Leave Days</span>
                <span className="text-2xl font-bold text-indigo-700">{formTotal}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={processing || (!editingEntitlement && formData.user_ids.length === 0)} className="rounded-full bg-slate-800 hover:bg-slate-700">
              {processing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingEntitlement ? 'Updating...' : 'Creating...'}</>
              ) : (
                editingEntitlement ? 'Update' : `Create ${formData.user_ids.length > 0 ? formData.user_ids.length + ' ' : ''}Entitlement${formData.user_ids.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Entitlement?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {deletingEntitlement && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200">
              <p className="font-medium text-slate-800">{getUserName(deletingEntitlement.user)}</p>
              <p className="text-sm text-slate-500 mt-1">Year {deletingEntitlement.year} - {getTotalDays(deletingEntitlement)} days total</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={processing} className="rounded-full bg-rose-600 hover:bg-rose-700">
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="h-4 w-4 mr-2" />Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
